import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipGrid, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';

import { IconDirective, TemplateContext, TypedTemplateDirective, provideValueAccessor } from '@core/shared/util';

import { AutocompletePanelComponent } from '../autocomplete-panel/autocomplete-panel.component';

export type AutocompleteInputAppearance = 'standard' | 'fill';

export type AutocompleteInputMode = 'input' | 'chip-item';

export type ChipItemTemplateContext<ImplicitType> = TemplateContext<ImplicitType>;

export type ChipItemTemplateRef<ImplicitType> = TemplateRef<TemplateContext<ImplicitType>>;

@Component({
  selector: 'mp-autocomplete-input',
  templateUrl: './autocomplete-input.component.html',
  styleUrl: './autocomplete-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    FormsModule,

    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatChipsModule,

    IconDirective,
    TypedTemplateDirective,
  ],
  providers: [provideValueAccessor(AutocompleteInputComponent)],
})
export class AutocompleteInputComponent<T> implements ControlValueAccessor {
  readonly selectedValue: ModelSignal<T | undefined> = model<T>();

  readonly autocompletePanel: InputSignal<AutocompletePanelComponent<T>> =
    input.required<AutocompletePanelComponent<T>>();

  readonly autocompleteInputMode: InputSignal<AutocompleteInputMode> = input<AutocompleteInputMode>('input');
  readonly appearance: InputSignal<AutocompleteInputAppearance> = input<AutocompleteInputAppearance>('fill');
  readonly inline: InputSignal<boolean> = input<boolean>(false);

  readonly disabled: ModelSignal<boolean> = model<boolean>(false);
  readonly required: InputSignal<boolean> = input<boolean>(false);
  readonly clearable: InputSignal<boolean> = input<boolean>(false);

  readonly autocompleteIcon: InputSignal<string | undefined> = input<string>();

  readonly label: InputSignal<string | undefined> = input<string>();
  readonly labelTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown>>();
  readonly placeholder: InputSignal<string | undefined> = input<string>();

  readonly errorMessage: InputSignal<string | undefined> = input<string>();
  readonly hint: InputSignal<string | undefined> = input<string>();

  readonly chipItemTemplate: InputSignal<ChipItemTemplateRef<T> | undefined> = input<ChipItemTemplateRef<T>>();

  readonly searchTermChange: OutputEmitterRef<string> = output<string>();

  readonly clear: OutputEmitterRef<void> = output<void>();

  readonly chipItemTemplateContextType!: ChipItemTemplateContext<T>;

  protected readonly inputElement: Signal<MatInput> = viewChild.required(MatInput);

  protected onChange = (_value: T | undefined) => {};

  protected onTouched = () => {};

  constructor(private readonly cdr: ChangeDetectorRef) {}

  onSearchTermChange(searchTerm: string): void {
    if (!searchTerm && this.selectedValue()) {
      this.selectedValue.set(undefined);
    }

    this.searchTermChange.emit(searchTerm);
  }

  onClear(): void {
    this.selectedValue.set(undefined);
    this.searchTermChange.emit('');

    this.clear.emit();
  }

  onSelectedValueChange(value: T | undefined): void {
    this.searchTermChange.emit('');
    this.selectedValue.set(value);
    this.onChange(value);
  }

  resetChipInput(input: HTMLInputElement, chipGrid: MatChipGrid): void {
    input.value = '';

    if (!this.selectedValue()) {
      // When selection is forced, the input value needs to be programmatically cleared without changes to the selected value.
      // To properly handle the floating label in this situation the form field control needs to be notified about the input value change.
      chipGrid.stateChanges.next();
    }
  }

  writeValue(value: T | undefined): void {
    this.selectedValue.set(value);
    // NOTE: Mark component for check is necessary as value accessor will not trigger that by default with reactive forms
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: T | undefined) => void) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  focus(): void {
    this.inputElement().focus();
  }
}
